/**
 * Internal dependencies
 */
import getVideoLengthDisplay from './getVideoLengthDisplay';

/**
 * The trimmed section of a video, on the source file's timeline.
 *
 * A resource is trimmed only when it has a `sourceEndSeconds`. An animated
 * thumbnail's `sourceOffsetSeconds` alone isn't a trim.
 *
 * @param {import('./resource').VideoResource} resource Video resource.
 * @return {{startSeconds: number, endSeconds: number, lengthSeconds: number}|null} The trim, or null.
 */
export function getVideoTrimRange(resource) {
  const { sourceOffsetSeconds, sourceEndSeconds } = resource || {};
  if (!Number.isFinite(sourceEndSeconds)) {
    return null;
  }

  const startSeconds =
    Number.isFinite(sourceOffsetSeconds) && sourceOffsetSeconds > 0
      ? sourceOffsetSeconds
      : 0;
  if (sourceEndSeconds <= startSeconds) {
    return null;
  }

  return {
    startSeconds,
    endSeconds: sourceEndSeconds,
    lengthSeconds: Math.round((sourceEndSeconds - startSeconds) * 1000) / 1000,
  };
}

/**
 * Apply a trim to a video resource. Selecting the whole video clears the trim.
 *
 * @param {import('./resource').VideoResource} resource Video resource.
 * @param {Object} range Trim range.
 * @param {number} range.startMs Start, in milliseconds into the source.
 * @param {number} range.endMs End, in milliseconds into the source.
 * @param {number} range.sourceLengthMs Length of the source, in milliseconds.
 * @return {import('./resource').VideoResource} The updated resource.
 */
export function applyVideoTrim(resource, { startMs, endMs, sourceLengthMs }) {
  const {
    sourceOffsetSeconds: _start,
    sourceEndSeconds: _end,
    ...untrimmedResource
  } = resource;

  if (startMs <= 0 && endMs >= sourceLengthMs) {
    if (!getVideoTrimRange(resource)) {
      return resource;
    }

    const length = Math.round(sourceLengthMs / 1000);
    return {
      ...untrimmedResource,
      length,
      lengthFormatted: getVideoLengthDisplay(length),
    };
  }

  const roundedStartMs = Math.round(startMs);
  const roundedEndMs = Math.round(endMs);
  const length = (roundedEndMs - roundedStartMs) / 1000;

  return {
    ...untrimmedResource,
    length,
    lengthFormatted: getVideoLengthDisplay(Math.round(length)),
    sourceOffsetSeconds: roundedStartMs / 1000,
    sourceEndSeconds: roundedEndMs / 1000,
  };
}
